import React from 'react';
import { StyleSheet, Text, View, Platform, TouchableOpacity } from 'react-native';

import raceIcon from '@assets/img/raceIcon.png';

import { moderateScale, isPhone, navigateTo } from '@helpers';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import Image from '@components/common/Image';
import { useHandleImageNavigation } from '@v2/pages/common/hooks/useHandleImageNavigation';
import { DOCUMENT_ENTITY_TYPE, IMAGE_HANDLING_CONTEXT } from '@components/imageHandling/common';
import Empty from '@components/common/Empty';
import { getImageSize, getItemDate, getDisplayOrderNumber, truncatePartCode } from './helpers';

const Item = ({ isEmpty, itemSize = 324, item, C }) => {
  const style = {
    opacity: isEmpty ? 0 : 1,
  };

  const { handleImageNavigation } = useHandleImageNavigation();

  if (itemSize) {
    style.width = itemSize;
  }

  const imgSize = getImageSize(itemSize);
  const currentMonth = getItemDate(item);
  const orderNumber = getDisplayOrderNumber(item);

  let imageLink = null;

  return isEmpty ? (
    <View style={[styles.itemComponent, style]} />
  ) : (
    <View style={[styles.itemComponent, style]}>
      <TouchableOpacity
        activeOpacity={1}
        onPress={() => {
          handleImageNavigation({
            [IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.Part]]: item.partId,
            [IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.ProductionOrder]]: item.productionOrderId,
          });
        }}
      >
        <View style={[imgSize, { backgroundColor: C('Background / Light / System') }]}>
          <View style={{ backgroundColor: C('Overlay/Light/Picture white'), width: '100%', height: '100%', position: 'absolute', zIndex: 3 }} />
          <View style={{ backgroundColor: C('Overlay/Light/Dim light'), width: '100%', height: '100%', position: 'absolute', zIndex: 2 }} />
          <Image
            source={{ uri: imageLink }}
            alt="img"
            style={{
              width: '100%',
              height: '100%',
              resizeMode: 'contain',
            }}
            resizeMode={'contain'}
            data={{
              [IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.Part]]: item.partId,
              [IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.ProductionOrder]]: item.productionOrderId,
            }}
          />
        </View>
      </TouchableOpacity>
      <View style={styles.itemImgMarksWrapper}>
        {item.new == 1 ? (
          <View style={[styles.itemStatusMark, { backgroundColor: C('Background / Light / New flag') }]}>
            <Text style={[styles.itemStatusMarkText, { color: C('Label / Light / AlwaysDark Primary') }, font.Small]}>Nieuw</Text>
          </View>
        ) : (
          <View />
        )}
        {currentMonth && (
          <View style={[styles.itemDateMark, { backgroundColor: C('Overlay / Light / Picture dark') }]}>
            <Image source={raceIcon} alt="icon" style={styles.itemDateIcon} />
            <Text style={[styles.itemDate, { color: C('Label / Light / AlwaysLight Primary') }, font.Headline]}>{currentMonth}</Text>
          </View>
        )}
      </View>
      <TouchableOpacity activeOpacity={1} onPress={() => (isPhone ? null : navigateTo('PanelView', item))}>
        <View style={styles.itemTextBlock}>
          <View style={styles.lineContainer}>
            <Text style={[styles.itemHeader, { color: C('Label / Light / Primary') }, font.Headline]}>
              {orderNumber}
              <Empty />
            </Text>
            <Text style={[styles.itemHeaderSize, styles.itemHeader, { color: C('Label / Light / Primary') }, font.Headline]}>5x</Text>
          </View>
          <Text
            numberOfLines={1}
            style={[styles.itemDescription, styles.itemLightText, { color: C('Label / Light / Secondary') }, font['Subhead 1']]}
          >
            {item.description}
            <Empty />
          </Text>
          <View style={[styles.lineContainer, styles.itemLightText, styles.itemPart]}>
            {item.partCode ? (
              <Text
                style={[styles.itemLightText, { color: C('Label / Light / Secondary') }, font['Subhead 1']]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                Part&nbsp;
                {truncatePartCode(item.partCode)}
                &nbsp;&nbsp;•&nbsp;&nbsp;{item.customerName}
                <Empty />
              </Text>
            ) : (
              <Text style={[styles.itemLightText, { color: C('Label / Light / Secondary') }, font['Subhead 1']]} numberOfLines={1}>
                {item.customerName}
                <Empty />
              </Text>
            )}
          </View>
          <Text style={[styles.itemDrawing, styles.itemLightText, { color: C('Label / Light / Secondary') }, font['Subhead 1']]} numberOfLines={1}>
            Tekening {item.drawVersion}
            <Empty />
          </Text>
        </View>
      </TouchableOpacity>
    </View>
  );
};

export default ColorWrapper(Item);

const styles = StyleSheet.create({
  lineContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  itemComponent: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: moderateScale(isPhone ? 24 : 29),
  },
  itemImgMarksWrapper: {
    display: 'flex',
    paddingLeft: 9,
    paddingRight: 9,
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 21,
    position: 'relative',
    top: -27,
    flexDirection: 'row',
  },
  itemStatusMark: {
    paddingLeft: 1,
    paddingRight: 1,
    height: 15,
    borderRadius: 2,
    marginTop: Platform.OS === 'android' ? 0 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemStatusMarkText: {
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: moderateScale(9.5),
    marginTop: Platform.OS === 'android' ? -1 : 1,
    letterSpacing: moderateScale(0.02),
    textTransform: 'uppercase',
    paddingLeft: 3,
    paddingRight: 3,
  },
  itemDateIcon: {
    height: 13,
    width: 13,
    marginLeft: Platform.OS === 'android' ? 1 : 0,
    marginTop: 1,
  },
  itemDateMark: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 11,
    height: 22,
    paddingLeft: 8,
    paddingRight: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  itemDate: {
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: moderateScale(16),
    lineHeight: moderateScale(19),
    letterSpacing: moderateScale(-0.3),
    marginLeft: 4,
  },

  itemTextBlock: {
    paddingLeft: 9,
    paddingTop: 7,
    position: 'relative',
    top: -21,
  },
  itemHeader: {
    display: 'flex',
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: !isPhone ? moderateScale(17) : 18,
    lineHeight: moderateScale(20),
    letterSpacing: moderateScale(0.02),
  },
  itemHeaderSize: {
    marginLeft: 7,
  },
  itemLightText: {
    fontFamily: Platform.OS === 'android' ? 'Roboto' : 'System',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: !isPhone ? moderateScale(15) : 16,
    lineHeight: moderateScale(18),
    letterSpacing: moderateScale(0.01),
  },
  itemDescription: {
    marginTop: 5,
  },
  itemPart: {
    marginTop: 5,
  },
  itemDrawing: {
    marginTop: 5,
  },
});
